import Box from "@mui/material/Box";
import { Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SERIES_COLORS } from "./palette";
import { withPartial } from "./granularity/granularity";

export interface BandPoint { x: string; label: string; realized?: number | null; base?: number | null; low?: number | null; high?: number | null; scenario?: number | null }
export type BandLineChartProps = { data: BandPoint[]; valueFormatter?: (v: number) => string; projectionFrom?: string; height?: number; width?: number; ariaLabel: string;
  /** Pontos (`x`) parciais: "(parcial)" no tooltip. */
  partialXs?: string[];
  /** Nomes das séries na legenda (padrão: os do Financeiro). */
  labels?: { realized?: string; base?: string; band?: string };
  /** Linha horizontal tracejada de meta (ex.: "Meta 200"). */
  target?: { value: number; label: string } | null };

const PROJECTION_FILL = "#eef1f4";
const SCENARIO_COLOR = "#7c5cd6";
const TARGET_COLOR = "#b26b00";
const DEFAULT_LABELS = { realized: "Realizado", base: "Base atual (média + agendados × conversão)", band: "Faixa de confiança 80%" };

/** Valor do tooltip; a faixa chega como par [low, high]. */
export const bandTooltipValue = (v: number | number[], valueFormatter?: (v: number) => string) => {
  const f = (n: number) => (valueFormatter ? valueFormatter(n) : n);
  return Array.isArray(v) ? v.map(f).join(" – ") : f(v);
};

// "Projeção de antecipado" (final-g1-financeiro.png): realizado contínuo, base tracejada, faixa de 80% e cenário pontilhado.
export function BandLineChart({ data, valueFormatter, projectionFrom, height = 240, width, ariaLabel, partialXs = [], labels, target = null }: BandLineChartProps) {
  const names = { ...DEFAULT_LABELS, ...labels };
  const partial = new Set(partialXs);
  const rows = data.map((d) => ({ ...d, band: d.low != null && d.high != null ? [d.low, d.high] : null }));
  const last = data[data.length - 1]?.x;
  const labelOf = (x: string) => data.find((d) => d.x === x)?.label ?? x;
  const chart = (
    <ComposedChart data={rows} width={width} height={height} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="x" tickFormatter={labelOf} />
      <YAxis tickFormatter={valueFormatter} />
      <Tooltip labelFormatter={(x: string) => withPartial(labelOf(x), partial.has(x))} formatter={(v: number | number[]) => bandTooltipValue(v, valueFormatter)} />
      <Legend />
      {projectionFrom && last && (
        <ReferenceArea x1={projectionFrom} x2={last} fill={PROJECTION_FILL} fillOpacity={0.7} label={{ value: "PROJEÇÃO", position: "insideBottom", fontSize: 10 }} />
      )}
      {target && (
        <ReferenceLine y={target.value} stroke={TARGET_COLOR} strokeDasharray="4 4" ifOverflow="extendDomain" label={{ value: target.label, position: "insideTopRight", fontSize: 11, fill: TARGET_COLOR }} />
      )}
      <Area dataKey="band" name={names.band} stroke="none" fill={SERIES_COLORS[1]} fillOpacity={0.2} isAnimationActive={false} />
      <Line dataKey="realized" name={names.realized} stroke={SERIES_COLORS[0]} strokeWidth={2} dot connectNulls={false} isAnimationActive={false} />
      <Line dataKey="base" name={names.base} stroke={SERIES_COLORS[1]} strokeWidth={2} strokeDasharray="6 4" dot connectNulls={false} isAnimationActive={false} />
      {data.some((d) => d.scenario != null) && (
        <Line dataKey="scenario" name="Cenário" stroke={SCENARIO_COLOR} strokeDasharray="2 3" dot connectNulls={false} isAnimationActive={false} />
      )}
    </ComposedChart>
  );
  return (
    <Box role="img" aria-label={ariaLabel}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
    </Box>
  );
}
